import { useLocalStorage } from "@vueuse/core";
import { defineStore } from "pinia";
import { reactive, ref, watch } from "vue";

import { getLanguages } from "../api/getLanguages";
import { getTranslations } from "../api/getTranslations";

export const useLocalesStore = defineStore("locale", () => {
    const currentLocale = useLocalStorage("locale", "ru");
    const SUPPORTED_LOCALES = ref([]);
    const translations = reactive({});
    const languages = [];
    const DEFAULT_LOCALE = ref(null);
    const translationsCache = useLocalStorage("translations_cache", {});
    const translationsVersion = useLocalStorage("translations_version", {});
    const loadingTranslations = ref(false);

    // Вспомогательные функции

    const validateTranslations = (data) => {
        return typeof data === "object" && data !== null;
    };
    const updateTranslations = (locale, data) => {
        translations[locale] = data[locale] || data;
    };
    const getBrowserLanguage = () => {
        // Получаем первый предпочитаемый язык пользователя
        const browserLang = navigator.language || navigator.userLanguage;

        return browserLang ? browserLang.split("-")[0].toLowerCase() : "ru";
    };

    const getManyTranslation = async (locale) => {
        const storageKey = `${locale}`;

        try {
            const cachedData = translationsCache.value[storageKey];

            if (cachedData) {
                if (validateTranslations(cachedData)) {
                    updateTranslations(locale, cachedData);

                    return cachedData;
                }

                delete translationsCache.value[storageKey];
            }
        } catch (e) {
            console.warn("Failed to access cached translations:", e);
            delete translationsCache.value[storageKey];
        }

        try {
            const freshData = await getTranslations(locale);

            translationsCache.value[storageKey] = freshData;
            updateTranslations(locale, freshData);

            return freshData;
        } catch (error) {
            console.error("Translation fetch failed:", error);
        }
    };

    // Проверка и обеспечение актуальности переводов
    const ensureFreshTranslations = async (locale, serverVersions) => {
        const serverVersion = serverVersions?.[locale] ?? null;
        const storedVersion = translationsVersion.value?.[locale] ?? null;

        if (serverVersion !== storedVersion) {
            delete translationsCache.value[locale];
            const fresh = await getManyTranslation(locale);

            translationsVersion.value[locale] = serverVersion;

            return fresh;
        }

        if (!translationsCache.value[locale]) {
            const fresh = await getManyTranslation(locale);

            translationsVersion.value[locale] = serverVersion;

            return fresh;
        }

        return translationsCache.value[locale];
    };

    // Основные функции

    const initLanguages = async () => {
        try {
            languages.value = await getLanguages();

            SUPPORTED_LOCALES.value = languages.value.map(
                ({ locale }) => locale
            );

            const serverVersions = Object.fromEntries(
                languages.value.map((l) => [
                    l.locale,
                    l.translations_version || null,
                ])
            );

            const browserLocale = getBrowserLanguage();

            DEFAULT_LOCALE.value = SUPPORTED_LOCALES.value.includes(
                browserLocale
            )
                ? browserLocale
                : SUPPORTED_LOCALES.value.includes("ru")
                ? "ru"
                : SUPPORTED_LOCALES.value[0];

            // Ensure current or default locale translations are fresh
            const desiredLocale = currentLocale.value || DEFAULT_LOCALE.value;

            await ensureFreshTranslations(desiredLocale, serverVersions);
        } catch (error) {
            console.error("Error initializing languages:", error);
            SUPPORTED_LOCALES.value = ["ru"];
            DEFAULT_LOCALE.value = "ru";
            await ensureFreshTranslations("ru", {});
        }
    };

    const clearTranslationsCache = () => {
        translationsCache.value = {};
        getManyTranslation(DEFAULT_LOCALE.value);
    };

    const setLocale = async (newLocale) => {
        if (
            SUPPORTED_LOCALES.value.includes(newLocale) &&
            currentLocale.value !== newLocale
        ) {
            loadingTranslations.value = true;

            try {
                const serverVersions = Object.fromEntries(
                    languages.value.map((l) => [
                        l.locale,
                        l.translations_version || null,
                    ])
                );

                await ensureFreshTranslations(newLocale, serverVersions);
                currentLocale.value = newLocale;
            } catch (error) {
                console.error(error);
            } finally {
                loadingTranslations.value = false;
                window.location.reload();
            }
        }
    };

    watch(
        currentLocale,
        (newLocale) => {
            if (typeof document !== "undefined" && document.documentElement) {
                document.documentElement.lang = newLocale || "ru";
            }
        },
        { immediate: true }
    );

    return {
        currentLocale,
        SUPPORTED_LOCALES,
        DEFAULT_LOCALE,
        translations,
        languages,
        loadingTranslations,
        initLanguages,
        getLanguages,
        getManyTranslation,
        clearTranslationsCache,
        setLocale,
    };
});
